import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { DateTime } from "luxon";
import "./Modal.css";
import "./OpeningHoursFixModal.css";
import Common from "../../Common/Common";
import Schedule from "../../Models/Schedule";
import { pushStartTimes } from "../../Common/pushStart";

// Two entry points:
//  - The schedule's "Closed"/"Opens at X"/"Closes at X" warning pill
//    (mode omitted/"conflict") — offers concrete fixes instead of just
//    pointing at the place's opening hours. Which trim option is offered
//    depends on the conflict shape (see Place.getOpeningHoursConflict): a
//    place closed all day has no same-day fix at all, so only Exchange
//    (and View opening hours) apply there.
//  - The event menu's "Swap or Replace Event" action (mode: "swap") —
//    available any time, not just when there's a conflict to resolve, so
//    it skips straight to the candidate list; no conflict-specific
//    messaging or push/pull options.
// "Swap" (candidates, onSwap) trades this event's time with another
// scheduled event's — two-way, both places must fit the other's slot.
// "Replace" (replaceCandidates, onReplace) substitutes this event's place
// for one that's in the guide but unused in any schedule — one-way, the
// event's own time never changes. See Schedule.js's findSwapCandidates/
// findReplaceCandidates.
// "Find a new place nearby" (onFindNew, onReplaceWithNew) searches outside
// the guide for a similar place open at this event's time — see
// GooglePlaces.findOpenAlternatives. Only runs when the user taps it, since
// it's a paid search.
const OpeningHoursFixModal = ({ show, onHide, event, conflict, candidates, replaceCandidates, replaceChecking, replaceLimited, mode, onPushStart, onPullEnd, onSwap, onReplace, onViewHours, onFindNew, onReplaceWithNew }) => {
  const isSwap = mode === "swap";
  const [step, setStep] = useState(isSwap ? "exchange" : "options");
  const [findState, setFindState] = useState("idle");
  const [foundPlaces, setFoundPlaces] = useState([]);
  // Ignores a search that finishes after the dialog was closed or reopened
  // for a different event.
  const findRequestId = useRef(0);

  useEffect(() => {
    if (show) setStep(isSwap ? "exchange" : "options");
    findRequestId.current++;
    setFindState("idle");
    setFoundPlaces([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, event]);

  const runFindNew = () => {
    const requestId = ++findRequestId.current;
    setFindState("loading");
    onFindNew((places) => {
      if (requestId !== findRequestId.current) return;
      setFoundPlaces(places || []);
      setFindState("done");
    });
  };

  if (!show || !event) return null;

  const canPushStart = !isSwap && conflict?.status === "closed" && !conflict.closedAllDay && typeof conflict.minutesUntilOpen === "number";
  const canPullEnd = !isSwap && conflict?.status === "closes_during" && typeof conflict.minutesUntilClose === "number";
  const openTimeISO = canPushStart
    ? DateTime.fromISO(event.Start, { zone: "utc" }).plus({ minutes: conflict.minutesUntilOpen }).toISO()
    : null;
  const closeTimeISO = canPullEnd
    ? DateTime.fromISO(event.Start, { zone: "utc" }).plus({ minutes: conflict.minutesUntilClose }).toISO()
    : null;

  const headline = !conflict
    ? null
    : conflict.closedAllDay
    ? "Closed all day"
    : canPushStart
    ? `Opens at ${Common.formatTimeBrief(openTimeISO)}`
    : canPullEnd
    ? `Closes at ${Common.formatTimeBrief(closeTimeISO)}`
    : "Closed";

  // Travel time between the conflicted event's place and each candidate's
  // place — shown in the exchange list so the user can judge whether a
  // swap makes geographic sense too, not just time-compatibility.
  // Schedule.js's findSwapCandidates already computes an equivalent
  // distance internally to sort candidates, but discards it before
  // returning bare events (other callers, like the auto-fix passes, don't
  // need it) — recomputed here for display only, in minutes, matching how
  // every other part of the schedule UI expresses distance (the gaps
  // between events) rather than raw km.
  const firstPlace = (placesStr) => {
    const id = (placesStr || "").split(",").map((x) => x.trim()).filter(Boolean)[0];
    return id ? window.guide_obj.places.getPlace(id) : null;
  };
  const eventPlace = firstPlace(event.Places);
  const travelMinutesToPlace = (candidatePlace) => {
    if (!eventPlace || !candidatePlace) return null;
    return Common.estimateTravelTimeMinutes(eventPlace, candidatePlace, 5);
  };
  const travelMinutesTo = (candidateEvent) => travelMinutesToPlace(firstPlace(candidateEvent.Places));
  // A candidate outside the area this event's day covers is still offered, but
  // flagged so it isn't picked by mistake (2026-11-03, Schedule.dayReachKm).
  const reachKm = eventPlace ? Schedule.dayReachKm(event, window.guide_obj.schedule?.events, window.guide_obj.places) : null;
  const far = (candidatePlace) =>
    reachKm != null && candidatePlace && candidatePlace.Lat != null &&
    Common.calculateDistance({ latitude: eventPlace.Lat, longitude: eventPlace.Longi }, { latitude: candidatePlace.Lat, longitude: candidatePlace.Longi }) > reachKm;
  const farNote = (candidatePlace) => (far(candidatePlace) ? <span className="ohf-far"> · Far from this day's other stops</span> : null);

  return (
    <div className="modal">
      <Modal show={show} onHide={onHide} animation={false} dialogClassName="ohf-dialog" aria-labelledby="ohf-title" centered>
        <Modal.Body style={{ padding: 0 }}>
          <div className="ohf-shell">
            <div className="ohf-header">
              <div className="ohf-header-icon">
                <FontAwesomeIcon icon={solid("triangle-exclamation")} />
              </div>
              <h2 className="ohf-title" id="ohf-title">{isSwap ? "Swap or Replace Event" : "Fix scheduling conflict"}</h2>
              <button className="ohf-close" onClick={onHide}>
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>

            {step === "options" ? (
              <>
                <div className="ohf-body">
                  <p className="ohf-message">{event.Subject} — {headline}.</p>

                  {canPushStart && (
                    <button className="ohf-option" onClick={onPushStart}>
                      <span className="ohf-option-title">Push start to {Common.formatTimeBrief(openTimeISO)}</span>
                      <span className="ohf-option-sub">{pushStartTimes(event, conflict.minutesUntilOpen).keepsEnd ? "Keep the same end time" : "Keep the same length"}</span>
                    </button>
                  )}

                  {canPullEnd && (
                    <button className="ohf-option" onClick={onPullEnd}>
                      <span className="ohf-option-title">Move end to {Common.formatTimeBrief(closeTimeISO)}</span>
                      <span className="ohf-option-sub">Keep the same start time</span>
                    </button>
                  )}

                  <button className="ohf-option" onClick={() => setStep("exchange")}>
                    <span className="ohf-option-title">Swap or replace</span>
                    <span className="ohf-option-sub">
                      {candidates.length > 0 || replaceCandidates.length > 0
                        ? [
                            candidates.length > 0 ? `${candidates.length} event${candidates.length === 1 ? "" : "s"} to swap with` : null,
                            replaceCandidates.length > 0 ? `${replaceCandidates.length} unused place${replaceCandidates.length === 1 ? "" : "s"} to replace with` : null,
                          ].filter(Boolean).join(" · ")
                        : "Trade times with another event or replace this place"}
                    </span>
                  </button>

                  <button className="ohf-option ohf-option-plain" onClick={onViewHours}>
                    <span className="ohf-option-title">View this place's opening hours</span>
                  </button>
                </div>
                <div className="ohf-footer">
                  <button className="ohf-btn ohf-btn-cancel" onClick={onHide}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div className="ohf-body">
                  {candidates.length === 0 && replaceCandidates.length === 0 ? (
                    <p className="ohf-message">
                      No compatible events or unused places found — a swap needs an event whose own place would also be fine at this event's time, and a replacement needs an unused guide place that's open at this time.
                    </p>
                  ) : (
                    <>
                      <p className="ohf-section-label">
                        Swap "{event.Subject}"'s time with another event
                      </p>
                      {candidates.length === 0 ? (
                        <p className="ohf-message">No compatible events found.</p>
                      ) : (
                        <div className="ohf-candidate-list">
                          {candidates.map((c) => {
                            const travelMinutes = travelMinutesTo(c);
                            return (
                            <button key={c.ID} className="ohf-option" onClick={() => onSwap(c)}>
                              <span className="ohf-option-title">{c.Subject}</span>
                              <span className="ohf-option-sub">
                                {DateTime.fromISO(c.Start, { zone: "utc" }).toFormat("ccc, MMM d")} · {Common.formatTimeLuxon(c.Start)} – {Common.formatTimeLuxon(c.End)}
                                {travelMinutes != null ? ` · ${Common.formatTravelAway(travelMinutes)}` : ""}
                                {farNote(firstPlace(c.Places))}
                              </span>
                            </button>
                            );
                          })}
                        </div>
                      )}

                      <p className="ohf-section-label">
                        Replace this event's place with an unused place
                      </p>
                      {replaceChecking && (
                        <p className="ohf-message">Checking opening times for these places…</p>
                      )}
                      {replaceLimited && (
                        <p className="ohf-message">Opening times couldn't be checked today (daily limit reached), so a place below may be closed at this time.</p>
                      )}
                      {replaceCandidates.length === 0 ? (
                        <p className="ohf-message">No unused places open at this event's time.</p>
                      ) : (
                        <div className="ohf-candidate-list">
                          {replaceCandidates.map((p) => {
                            const travelMinutes = travelMinutesToPlace(p);
                            return (
                            <button key={p.ID} className="ohf-option" onClick={() => onReplace(p)}>
                              <span className="ohf-option-title">{p.Name}</span>
                              <span className="ohf-option-sub">
                                Not currently used in any schedule
                                {travelMinutes != null ? ` · ${Common.formatTravelAway(travelMinutes)}` : ""}
                                {farNote(p)}
                              </span>
                            </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}

                  {onFindNew && (
                    <>
                      <p className="ohf-section-label">Replace with a new place nearby</p>
                      {findState === "idle" && (
                        <button className="ohf-option" onClick={runFindNew}>
                          <span className="ohf-option-title">Find a new place nearby</span>
                          <span className="ohf-option-sub">Searches for similar places open at this event's time</span>
                        </button>
                      )}
                      {findState === "loading" && <p className="ohf-message">Searching nearby…</p>}
                      {findState === "done" && foundPlaces.length === 0 && (
                        <p className="ohf-message">No similar places nearby are open at this time.</p>
                      )}
                      {findState === "done" && foundPlaces.length > 0 && (
                        <div className="ohf-candidate-list">
                          {foundPlaces.map((p, i) => {
                            const travelMinutes = travelMinutesToPlace(p);
                            return (
                              <button key={`${p.Name}-${i}`} className="ohf-option" onClick={() => onReplaceWithNew(p)}>
                                <span className="ohf-option-title">{p.Name}</span>
                                <span className="ohf-option-sub">
                                  {p.Rating ? `★ ${p.Rating}` : "Open at this time"}
                                  {travelMinutes != null ? ` · ${Common.formatTravelAway(travelMinutes)}` : ""}
                                  {farNote(p)}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="ohf-footer">
                  {isSwap ? (
                    <button className="ohf-btn ohf-btn-cancel" onClick={onHide}>Cancel</button>
                  ) : (
                    <button className="ohf-btn ohf-btn-cancel" onClick={() => setStep("options")}>Back</button>
                  )}
                </div>
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default OpeningHoursFixModal;
